import { useQuery } from '@tanstack/react-query';
import { MESSAGES } from '@lib/constants/messages';
import { useCompanies } from '@features/companies/hooks/useCompanies';
import { useBranchOffices } from '@features/branchOffice/hooks/useBranchOffices';
import { subscriptionService } from '../services/subscriptionService';
import { useSubscription } from './useSubscription';
import type {
  CompanyConsumption,
  ValidationResult
} from '../types/subscription.types';

// When false (default), all consumption validations return allowed:true.
// Set VITE_SUBSCRIPTIONS_ENABLED=true in the environment to enforce plan limits.
const SUBSCRIPTIONS_ENABLED = import.meta.env.VITE_SUBSCRIPTIONS_ENABLED === 'true';

/**
 * Hook to get company consumption data
 */
export function useCompanyConsumption(year?: number, month?: number) {
  const { selectedCompany } = useCompanies({ navigateOnSelect: false });

  return useQuery<CompanyConsumption>({
    queryKey: ['consumption', selectedCompany?.id, year, month],
    queryFn: async () => {
      if (!selectedCompany?.id) {
        throw new Error(MESSAGES.NO_COMPANY_SELECTED);
      }

      console.log('🔍 Fetching consumption for:', {
        companyId: selectedCompany.id,
        year,
        month,
      });

      try {
        const result = await subscriptionService.getCompanyConsumption(
          selectedCompany.id,
          year,
          month
        );
        console.log('✅ Consumption fetched successfully:', result);
        return result;
      } catch (error: any) {
        console.error('❌ Error fetching consumption:', {
          error,
          status: error?.response?.status,
          data: error?.response?.data,
        });
        throw error;
      }
    },
    enabled: !!selectedCompany?.id && SUBSCRIPTIONS_ENABLED,
    staleTime: 5 * 60 * 1000, // 5 minutes - invalidated on create/delete mutations
    retry: false, // Disable retry to see error immediately
  });
}

/**
 * Main hook that combines consumption and subscription data
 * Provides validation methods based on plan limits and current consumption
 */
export function useConsumption(year?: number, month?: number) {
  const consumptionQuery = useCompanyConsumption(year, month);
  const { planLimits, currentPlan, isLoading: isLoadingSubscription } = useSubscription();
  // Solo necesitamos el CONTEO de sucursales activas, no la lista. Pedimos
  // pageSize:1 + activeOnly → el backend devuelve el total de activas en `total`
  // sin traer todas las filas (elimina el pull de 9999 que saturaba Aludra).
  const branchOfficesQuery = useBranchOffices({ page: 1, pageSize: 1, activeOnly: true });

  const consumption = consumptionQuery.data;
  const dataReady = !!consumption && !!planLimits;

  // El backend ya cuenta solo activas (activeOnly) → usamos el total directo.
  // Más confiable que consumption.BranchOfficeQty (incluye soft-deleted).
  const activeBranchOfficeCount = branchOfficesQuery.data?.total ?? 0;

  // When data is still loading, allow the action (backend enforces real limits).
  // Only block when data has loaded and limits are actually exceeded.
  const loadingResult: ValidationResult = { allowed: true };
  // Si no se pudo verificar el plan, NO bloquear al usuario.
  // El backend (Aludra) valida los límites reales al emitir la factura.
  const errorResult: ValidationResult = { allowed: true };

  // Validation methods
  const validateCanAddUser = (): ValidationResult => {
    if (!SUBSCRIPTIONS_ENABLED) return { allowed: true };
    if (!dataReady) {
      return isLoadingSubscription || consumptionQuery.isLoading ? loadingResult : errorResult;
    }
    return subscriptionService.validateCanAddUser(consumption, planLimits);
  };

  const validateCanAddInvoice = (isMonthly: boolean = true): ValidationResult => {
    if (!SUBSCRIPTIONS_ENABLED) return { allowed: true };
    if (!dataReady) {
      return isLoadingSubscription || consumptionQuery.isLoading ? loadingResult : errorResult;
    }
    return subscriptionService.validateCanAddInvoice(consumption, planLimits, isMonthly);
  };

  const canModifyTemplate = (templateType: 'cafe' | 'email'): boolean => {
    if (!SUBSCRIPTIONS_ENABLED) return true;
    if (!planLimits) return !isLoadingSubscription ? false : true;
    return subscriptionService.canModifyTemplate(planLimits, templateType);
  };

  const canModifyLogo = (): boolean => {
    if (!SUBSCRIPTIONS_ENABLED) return true;
    if (!planLimits) return !isLoadingSubscription ? false : true;
    return subscriptionService.canModifyLogo(planLimits);
  };

  // Get remaining resources
  const getRemainingUsers = (): number | null => {
    if (!consumption || !planLimits) return null;

    // -1 means unlimited
    if (planLimits.usuarios === -1) return -1;

    // Validate that both values are valid numbers
    const limit = Number(planLimits.usuarios) || 0;
    const used = Number(consumption.UserQty) || 0;

    return Math.max(0, limit - used);
  };

  const getRemainingInvoices = (isMonthly: boolean = true): number | null => {
    if (!consumption || !planLimits) return null;

    const limit = isMonthly ? planLimits.facturas_mes : planLimits.facturas_annual;
    const current = isMonthly ? consumption.MonthlyInvoiceQty : consumption.AnnualInvoiceQty;

    // -1 or undefined means unlimited
    if (!limit || limit === -1) return -1;

    return Math.max(0, limit - current);
  };

  const validateCanAddBranchOffice = (): ValidationResult => {
    if (!SUBSCRIPTIONS_ENABLED) return { allowed: true };
    if (!dataReady) {
      return isLoadingSubscription || consumptionQuery.isLoading ? loadingResult : errorResult;
    }
    // Use real active count instead of consumption.BranchOfficeQty (unreliable)
    const patchedConsumption: CompanyConsumption = {
      ...consumption,
      BranchOfficeQty: activeBranchOfficeCount,
    };
    return subscriptionService.validateCanAddBranchOffice(patchedConsumption, planLimits);
  };

  const getRemainingBranchOffices = (): number | null => {
    if (!consumption || !planLimits) return null;

    // -1 means unlimited
    if (planLimits.branch_office === -1) return -1;

    return Math.max(0, planLimits.branch_office - activeBranchOfficeCount);
  };

  const validateCanAddProduct = (): ValidationResult => {
    if (!SUBSCRIPTIONS_ENABLED) return { allowed: true };
    if (!dataReady) {
      return isLoadingSubscription || consumptionQuery.isLoading ? loadingResult : errorResult;
    }
    return subscriptionService.validateCanAddProduct(consumption, planLimits);
  };

  const getRemainingProducts = (): number | null => {
    if (!consumption || !planLimits) return null;

    // -1 means unlimited
    if (planLimits.products === -1) return -1;

    return Math.max(0, planLimits.products - consumption.ProductQty);
  };

  const validateCanAddCustomer = (): ValidationResult => {
    if (!SUBSCRIPTIONS_ENABLED) return { allowed: true };
    if (!dataReady) {
      return isLoadingSubscription || consumptionQuery.isLoading ? loadingResult : errorResult;
    }
    return subscriptionService.validateCanAddCustomer(consumption, planLimits);
  };

  const getRemainingCustomers = (): number | null => {
    if (!consumption || !planLimits) return null;

    // -1 means unlimited
    if (planLimits.customers === -1) return -1;

    return Math.max(0, planLimits.customers - consumption.CustomerQty);
  };

  return {
    // Consumption data
    consumption,
    isLoading: consumptionQuery.isLoading || isLoadingSubscription,
    isError: consumptionQuery.isError,
    error: consumptionQuery.error,

    // Plan information
    currentPlan,
    planLimits,

    // Validation methods
    validateCanAddUser,
    validateCanAddInvoice,
    validateCanAddBranchOffice,
    validateCanAddProduct,
    validateCanAddCustomer,
    canModifyTemplate,
    canModifyLogo,

    // Resource counts
    getRemainingUsers,
    getRemainingInvoices,
    getRemainingBranchOffices,
    getRemainingProducts,
    getRemainingCustomers,

    // Refetch
    refetch: consumptionQuery.refetch,
  };
}
